import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const defaultColors = {
  primary: '#667eea',
  primaryHover: '#5a67d8',
  background: '#f7fafc',
  surface: '#ffffff',
  textPrimary: '#2d3748',
  textSecondary: '#718096'
};

const AdminDashboard = () => {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDomainForm, setShowDomainForm] = useState(false);
  const [editingDomainId, setEditingDomainId] = useState(null);
  const [domainForm, setDomainForm] = useState({
    hostname: '',
    name: '',
    logoUrl: '',
    colors: { ...defaultColors },
    snippets: [],
    termsUrl: '',
    privacyUrl: '',
    legalUrl: '',
    isActive: true
  });
  const [snippetsJson, setSnippetsJson] = useState('[]');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      // Load configuration
      const configResponse = await fetch('/api/admin/config', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!configResponse.ok) {
        throw new Error('Failed to load configuration');
      }
      
      const configData = await configResponse.json();
      setConfig(configData);
      
      // Load statistics
      const statsResponse = await fetch('/api/admin/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!statsResponse.ok) {
        throw new Error('Failed to load statistics');
      }
      
      const statsData = await statsResponse.json();
      setStats(statsData);

      const domainsResponse = await fetch('/api/admin/domains', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (domainsResponse.ok) {
        const domainsData = await domainsResponse.json();
        setDomains(domainsData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar los datos'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAuth = async (authType, enabled) => {
    try {
      setSaving(true);
      
      // Show confirmation dialog for disabling methods
      if (!enabled) {
        const result = await Swal.fire({
          title: '¿Estás seguro?',
          text: `¿Quieres desactivar la autenticación por ${getAuthMethodName(authType)}?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#e74c3c',
          cancelButtonColor: '#6c757d',
          confirmButtonText: 'Sí, desactivar',
          cancelButtonText: 'Cancelar'
        });
        
        if (!result.isConfirmed) {
          return;
        }
      }
      
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          [authType]: { enabled }
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update configuration');
      }
      
      const updatedConfig = await response.json();
      setConfig(updatedConfig.config);
      
      // Show success message
      const methodName = getAuthMethodName(authType);
      const action = enabled ? 'activado' : 'desactivado';
      
      Swal.fire({
        icon: 'success',
        title: 'Configuración actualizada',
        text: `Autenticación por ${methodName} ${action} correctamente`,
        timer: 2000,
        showConfirmButton: false
      });
      
    } catch (error) {
      console.error('Error updating config:', error);
      
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'No se pudo actualizar la configuración'
      });
    } finally {
      setSaving(false);
    }
  };

  const getAuthMethodName = (authType) => {
    const names = {
      phoneAuth: 'teléfono',
      emailLinkAuth: 'email',
      googleAuth: 'Google'
    };
    return names[authType] || authType;
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    navigate('/admin/login');
  };

  const openNewDomainForm = () => {
    setEditingDomainId(null);
    setDomainForm({
      hostname: '',
      name: '',
      logoUrl: '',
      colors: { ...defaultColors },
      snippets: [],
      termsUrl: '',
      privacyUrl: '',
      legalUrl: '',
      isActive: true
    });
    setSnippetsJson('[]');
    setShowDomainForm(true);
  };

  const openEditDomainForm = (d) => {
    setEditingDomainId(d._id);
    setDomainForm({
      hostname: d.hostname,
      name: d.name,
      logoUrl: d.logoUrl || '',
      colors: { ...defaultColors, ...d.colors },
      snippets: Array.isArray(d.snippets) ? d.snippets : [],
      termsUrl: d.termsUrl || '',
      privacyUrl: d.privacyUrl || '',
      legalUrl: d.legalUrl || '',
      isActive: d.isActive !== false
    });
    setSnippetsJson(JSON.stringify(Array.isArray(d.snippets) ? d.snippets : [], null, 2));
    setShowDomainForm(true);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setSaving(true);
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/admin/upload-logo', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al subir');
      }
      const data = await res.json();
      setDomainForm(prev => ({ ...prev, logoUrl: data.imageUrl }));
      Swal.fire({ icon: 'success', title: 'Logo subido', timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDomain = async (e) => {
    e.preventDefault();
    let snippets = [];
    try {
      snippets = JSON.parse(snippetsJson);
    } catch {
      Swal.fire({ icon: 'error', title: 'Snippets inválidos', text: 'Debe ser un JSON array' });
      return;
    }
    try {
      setSaving(true);
      const token = localStorage.getItem('adminToken');
      const body = {
        ...domainForm,
        snippets,
        hostname: domainForm.hostname.trim().toLowerCase()
      };
      const url = editingDomainId
        ? `/api/admin/domains/${editingDomainId}`
        : '/api/admin/domains';
      const method = editingDomainId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || err.error || 'Error al guardar');
      }
      setShowDomainForm(false);
      loadData();
      Swal.fire({ icon: 'success', title: editingDomainId ? 'Dominio actualizado' : 'Dominio creado', timer: 2000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDomain = async (id) => {
    const result = await Swal.fire({
      title: '¿Eliminar dominio?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar'
    });
    if (!result.isConfirmed) return;
    try {
      setSaving(true);
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/domains/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al eliminar');
      setShowDomainForm(false);
      loadData();
      Swal.fire({ icon: 'success', title: 'Dominio eliminado', timer: 2000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingCard>
          <LoadingSpinner />
          <p>Cargando panel de administración...</p>
        </LoadingCard>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderContent>
          <Title>Panel de Administración - Liga Estrella</Title>
          <LogoutButton onClick={handleLogout}>
            Cerrar Sesión
          </LogoutButton>
        </HeaderContent>
      </Header>

      <Content>
        <Section>
          <SectionTitle>📊 Estadísticas</SectionTitle>
          <StatsGrid>
            <StatCard>
              <StatNumber>{stats?.totalUsers || 0}</StatNumber>
              <StatLabel>Usuarios Totales</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{stats?.newUsersToday || 0}</StatNumber>
              <StatLabel>Nuevos Hoy</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{config?.phoneAuth?.enabled ? '✅' : '❌'}</StatNumber>
              <StatLabel>Teléfono</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{config?.emailLinkAuth?.enabled ? '✅' : '❌'}</StatNumber>
              <StatLabel>Email</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{config?.googleAuth?.enabled ? '✅' : '❌'}</StatNumber>
              <StatLabel>Google</StatLabel>
            </StatCard>
          </StatsGrid>
        </Section>

        <Section>
          <SectionTitle>⚙️ Configuración de Autenticación</SectionTitle>
          <ConfigGrid>
            <ConfigCard>
              <ConfigHeader>
                <ConfigTitle>
                  <ConfigIcon>📞</ConfigIcon>
                  {config?.phoneAuth?.name}
                </ConfigTitle>
                <ToggleSwitch
                  checked={config?.phoneAuth?.enabled}
                  onChange={(e) => handleToggleAuth('phoneAuth', e.target.checked)}
                  disabled={saving}
                >
                  <ToggleInput type="checkbox" />
                </ToggleSwitch>
              </ConfigHeader>
              <ConfigDescription>{config?.phoneAuth?.description}</ConfigDescription>
              <ConfigStatus enabled={config?.phoneAuth?.enabled}>
                {config?.phoneAuth?.enabled ? 'Activado' : 'Desactivado'}
              </ConfigStatus>
            </ConfigCard>

            <ConfigCard>
              <ConfigHeader>
                <ConfigTitle>
                  <ConfigIcon>✉️</ConfigIcon>
                  {config?.emailLinkAuth?.name}
                </ConfigTitle>
                <ToggleSwitch
                  checked={config?.emailLinkAuth?.enabled}
                  onChange={(e) => handleToggleAuth('emailLinkAuth', e.target.checked)}
                  disabled={saving}
                >
                  <ToggleInput type="checkbox" />
                </ToggleSwitch>
              </ConfigHeader>
              <ConfigDescription>{config?.emailLinkAuth?.description}</ConfigDescription>
              <ConfigStatus enabled={config?.emailLinkAuth?.enabled}>
                {config?.emailLinkAuth?.enabled ? 'Activado' : 'Desactivado'}
              </ConfigStatus>
            </ConfigCard>

            <ConfigCard>
              <ConfigHeader>
                <ConfigTitle>
                  <ConfigIcon>🔗</ConfigIcon>
                  {config?.googleAuth?.name}
                </ConfigTitle>
                <ToggleSwitch
                  checked={config?.googleAuth?.enabled}
                  onChange={(e) => handleToggleAuth('googleAuth', e.target.checked)}
                  disabled={saving}
                >
                  <ToggleInput type="checkbox" />
                </ToggleSwitch>
              </ConfigHeader>
              <ConfigDescription>{config?.googleAuth?.description}</ConfigDescription>
              <ConfigStatus enabled={config?.googleAuth?.enabled}>
                {config?.googleAuth?.enabled ? 'Activado' : 'Desactivado'}
              </ConfigStatus>
            </ConfigCard>
          </ConfigGrid>
        </Section>

        <Section>
          <SectionTitle>ℹ️ Información del Sistema</SectionTitle>
          <InfoCard>
            <InfoRow>
              <InfoLabel>Última actualización:</InfoLabel>
              <InfoValue>
                {config?.lastUpdated ? new Date(config.lastUpdated).toLocaleString() : 'N/A'}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Actualizado por:</InfoLabel>
              <InfoValue>{config?.updatedBy || 'Sistema'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Registro permitido:</InfoLabel>
              <InfoValue>{config?.allowRegistration ? '✅ Sí' : '❌ No'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Perfil obligatorio:</InfoLabel>
              <InfoValue>{config?.requireProfileCompletion ? '✅ Sí' : '❌ No'}</InfoValue>
            </InfoRow>
          </InfoCard>
        </Section>

        <Section>
          <SectionTitle>🌐 Dominios</SectionTitle>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem', fontSize: '0.9rem' }}>
            Configura logo, nombre, colores, scripts de cookies y enlaces legales por dominio (ej: auth.pamplonews.com, auth.bilbo.news).
          </p>
          <DomainToolbar>
            <AddDomainButton onClick={openNewDomainForm}>+ Añadir dominio</AddDomainButton>
          </DomainToolbar>
          <DomainsList>
            {domains.length === 0 && !showDomainForm && (
              <EmptyDomains>No hay dominios configurados. Añade uno para personalizar la página de login por dominio.</EmptyDomains>
            )}
            {domains.map(d => (
              <DomainCard key={d._id}>
                <DomainCardLeft>
                  {d.logoUrl ? <DomainLogo src={d.logoUrl} alt="" /> : <DomainLogoPlaceholder>Logo</DomainLogoPlaceholder>}
                  <DomainInfo>
                    <DomainHostname>{d.hostname}</DomainHostname>
                    <DomainName>{d.name}</DomainName>
                  </DomainInfo>
                </DomainCardLeft>
                <DomainCardActions>
                  <EditDomainButton onClick={() => openEditDomainForm(d)}>Editar</EditDomainButton>
                  <DeleteDomainButton onClick={() => handleDeleteDomain(d._id)}>Eliminar</DeleteDomainButton>
                </DomainCardActions>
              </DomainCard>
            ))}
          </DomainsList>

          {showDomainForm && (
            <DomainFormCard onSubmit={handleSaveDomain}>
              <DomainFormTitle>{editingDomainId ? 'Editar dominio' : 'Nuevo dominio'}</DomainFormTitle>
              <FormRow>
                <FormField>
                  <FormLabel>Hostname *</FormLabel>
                  <FormInput
                    value={domainForm.hostname}
                    onChange={e => setDomainForm(prev => ({ ...prev, hostname: e.target.value }))}
                    placeholder="auth.pamplonews.com"
                    required
                    disabled={!!editingDomainId}
                  />
                </FormField>
                <FormField>
                  <FormLabel>Nombre</FormLabel>
                  <FormInput
                    value={domainForm.name}
                    onChange={e => setDomainForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Pamplonews"
                  />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField>
                  <FormLabel>Logo</FormLabel>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {domainForm.logoUrl && <DomainLogo src={domainForm.logoUrl} alt="" style={{ width: 60, height: 60 }} />}
                    <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={saving} />
                  </div>
                </FormField>
              </FormRow>
              <FormRow>
                <FormField>
                  <FormLabel>Color primario</FormLabel>
                  <FormInput
                    type="text"
                    value={domainForm.colors.primary}
                    onChange={e => setDomainForm(prev => ({ ...prev, colors: { ...prev.colors, primary: e.target.value } }))}
                    placeholder="#667eea"
                  />
                </FormField>
                <FormField>
                  <FormLabel>Color primario (hover)</FormLabel>
                  <FormInput
                    type="text"
                    value={domainForm.colors.primaryHover}
                    onChange={e => setDomainForm(prev => ({ ...prev, colors: { ...prev.colors, primaryHover: e.target.value } }))}
                    placeholder="#5a67d8"
                  />
                </FormField>
              </FormRow>
              <FormField>
                <FormLabel>Enlace términos y condiciones</FormLabel>
                <FormInput
                  type="url"
                  value={domainForm.termsUrl}
                  onChange={e => setDomainForm(prev => ({ ...prev, termsUrl: e.target.value }))}
                  placeholder="https://..."
                />
              </FormField>
              <FormField>
                <FormLabel>Enlace política de privacidad</FormLabel>
                <FormInput
                  type="url"
                  value={domainForm.privacyUrl}
                  onChange={e => setDomainForm(prev => ({ ...prev, privacyUrl: e.target.value }))}
                  placeholder="https://..."
                />
              </FormField>
              <FormField>
                <FormLabel>Enlace aviso legal</FormLabel>
                <FormInput
                  type="url"
                  value={domainForm.legalUrl}
                  onChange={e => setDomainForm(prev => ({ ...prev, legalUrl: e.target.value }))}
                  placeholder="https://..."
                />
              </FormField>
              <FormField>
                <FormLabel>Snippets (JSON array de scripts, ej. cookies)</FormLabel>
                <FormTextarea
                  value={snippetsJson}
                  onChange={e => setSnippetsJson(e.target.value)}
                  placeholder={'[{"tag":"script","src":"https://...","data-id":"...","async":true}]'}
                  rows={6}
                />
              </FormField>
              <FormRow>
                <FormField style={{ flex: 0 }}>
                  <FormLabel>
                    <input
                      type="checkbox"
                      checked={domainForm.isActive}
                      onChange={e => setDomainForm(prev => ({ ...prev, isActive: e.target.checked }))}
                    />
                    {' '}Activo
                  </FormLabel>
                </FormField>
              </FormRow>
              <DomainFormActions>
                <SaveDomainButton type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</SaveDomainButton>
                <CancelDomainButton type="button" onClick={() => setShowDomainForm(false)}>Cancelar</CancelDomainButton>
              </DomainFormActions>
            </DomainFormCard>
          )}
        </Section>
      </Content>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: var(--background);
`;

const Header = styled.header`
  background: var(--surface);
  border-bottom: 1px solid var(--border-light);
  padding: 1rem 0;
  box-shadow: var(--shadow-sm);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
`;

const LogoutButton = styled.button`
  background: var(--primary-color);
  border: none;
  color: var(--text-light);
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: var(--transition);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;

  &:hover {
    background: var(--primary-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-lg);
  }
`;

const Content = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Section = styled.div`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
`;

const StatCard = styled.div`
  background: var(--surface);
  border-radius: var(--border-radius-lg);
  padding: 2rem;
  text-align: center;
  box-shadow: var(--shadow);
  border: 1px solid var(--border-light);
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 500;
`;

const ConfigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 60px;
  height: 30px;
  background: ${props => props.checked ? 'var(--primary-color)' : 'var(--border)'};
  border-radius: 15px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: var(--transition);
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover:not(:disabled) {
    transform: scale(1.05);
  }

  &::before {
    content: '';
    position: absolute;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: white;
    top: 3px;
    left: ${props => props.checked ? '33px' : '3px'};
    transition: var(--transition);
    box-shadow: var(--shadow-sm);
  }

  &::after {
    content: ${props => props.checked ? '"ON"' : '"OFF"'};
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.7rem;
    font-weight: 600;
    color: white;
    left: ${props => props.checked ? '8px' : '35px'};
    transition: var(--transition);
  }
`;

const ToggleInput = styled.input`
  display: none;
`;

const ConfigCard = styled.div`
  background: var(--surface);
  border-radius: var(--border-radius-lg);
  padding: 1.5rem;
  box-shadow: var(--shadow);
  border: 1px solid var(--border-light);
  transition: var(--transition);
  
  &:hover {
    box-shadow: var(--shadow-lg);
    transform: translateY(-2px);
  }
`;

const ConfigHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ConfigTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ConfigIcon = styled.span`
  font-size: 1.2rem;
`;

const ConfigDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.4;
`;

const ConfigStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.5rem;
  border-radius: var(--border-radius-sm);
  background: ${props => props.enabled ? 'var(--success-light)' : 'var(--danger-light)'};
  color: ${props => props.enabled ? 'var(--success-dark)' : 'var(--danger-dark)'};
  font-size: 0.8rem;
  font-weight: 600;
`;

const InfoCard = styled.div`
  background: var(--surface);
  border-radius: var(--border-radius-lg);
  padding: 1.5rem;
  box-shadow: var(--shadow);
  border: 1px solid var(--border-light);
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border-light);

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 0.9rem;
`;

const InfoValue = styled.span`
  color: var(--text-primary);
  font-weight: 500;
`;

const LoadingCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--surface);
  border-radius: var(--border-radius-lg);
  padding: 3rem;
  margin: 2rem auto;
  max-width: 400px;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-light);

  p {
    margin-top: 1rem;
    color: var(--text-primary);
    font-size: 1.1rem;
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-light);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const DomainToolbar = styled.div`
  margin-bottom: 1rem;
`;

const AddDomainButton = styled.button`
  background: var(--primary-color);
  color: var(--text-light);
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  &:hover { background: var(--primary-hover); }
`;

const DomainsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const EmptyDomains = styled.p`
  color: var(--text-secondary);
  font-style: italic;
  padding: 1.5rem;
  background: var(--surface);
  border-radius: var(--border-radius);
  border: 1px dashed var(--border);
`;

const DomainCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: var(--surface);
  border-radius: var(--border-radius);
  border: 1px solid var(--border-light);
`;

const DomainCardLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const DomainLogo = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
  border-radius: var(--border-radius);
`;

const DomainLogoPlaceholder = styled.div`
  width: 48px;
  height: 48px;
  background: var(--border-light);
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const DomainInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DomainHostname = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

const DomainName = styled.span`
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const DomainCardActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const EditDomainButton = styled.button`
  padding: 0.5rem 1rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  font-size: 0.85rem;
  &:hover { opacity: 0.9; }
`;

const DeleteDomainButton = styled.button`
  padding: 0.5rem 1rem;
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  font-size: 0.85rem;
  &:hover { opacity: 0.9; }
`;

const DomainFormCard = styled.form`
  background: var(--surface);
  border-radius: var(--border-radius-lg);
  padding: 2rem;
  margin-top: 1rem;
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow);
`;

const DomainFormTitle = styled.h3`
  margin: 0 0 1.5rem 0;
  color: var(--text-primary);
  font-size: 1.25rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const FormField = styled.div`
  flex: 1;
  min-width: 200px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.35rem;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: var(--border-radius);
  font-size: 0.95rem;
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: var(--border-radius);
  font-size: 0.9rem;
  font-family: monospace;
`;

const DomainFormActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

const SaveDomainButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { background: var(--primary-hover); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const CancelDomainButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--border);
  color: var(--text-primary);
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  &:hover { background: var(--border-light); }
`;

export default AdminDashboard; 